import { AfterContentChecked, AfterViewInit, Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import {AlertController, IonContent, IonicModule, ViewDidEnter} from "@ionic/angular";
import {NgxExtendedPdfViewerModule, NgxExtendedPdfViewerService, PagesLoadedEvent} from "ngx-extended-pdf-viewer";
import {SharedModalControllerService} from "@shared/services/shared-modal-controller.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {AsyncPipe} from "@angular/common";
import {IViewerBlobSrc} from "@shared/components/viewers/interfaces/viewer-src.interface";
import {Capacitor} from "@capacitor/core";
import {LoadingController} from '@ionic/angular';
import {Share} from "@capacitor/share";
import {async, BehaviorSubject} from "rxjs";

@Component({
  selector: 'app-viewer-pdf',
  templateUrl: './shared-viewer-pdf.component.html',
  styleUrls: ['./shared-viewer-pdf.component.scss'],
  imports: [
    IonicModule,
    NgxExtendedPdfViewerModule,
    TranslatePipe,
    AsyncPipe
],
  standalone: true
})
export class SharedViewerPdfComponent implements AfterViewInit, ViewDidEnter {
  private _sharedModalControllerService = inject(SharedModalControllerService);
  private _pdfService = inject(NgxExtendedPdfViewerService);
  private _loadingController = inject(LoadingController);
  private _translateService = inject(TranslateService);
  private _alertController = inject(AlertController);


  @Input() file!: IViewerBlobSrc;
  @Input() name!: string;
  @Input() downloadUri?: string;
  @Input() isDownloadEnabled?: boolean = false;

  zoomLevel!: string;
  zoomPercentage: number;
  isTextSearch: boolean;
  currentPage: number;
  pagesCount: number;
  isSidebarVisible: boolean;
  historyKeyValue: string;
  viewerHeightSubject: BehaviorSubject<string> = new BehaviorSubject<string>('100%');

  constructor() {
    this.zoomPercentage = 100;
    this.isTextSearch = false;
    this.currentPage = 1;
    this.pagesCount = 1;
    this.isSidebarVisible = false;
    this.historyKeyValue = 'pdfjs.history';
  }

  ngAfterViewInit(): void {
    this._removePdfHistory();
    this._initResizeListener();
  }

  ionViewDidEnter(): void {
    this.onResizeWorkaround();
  }

  private _initResizeListener(): void {
    window.addEventListener("orientationchange", () => this.onResizeWorkaround);
    screen.orientation.addEventListener("change", () => this.onResizeWorkaround);
  }

  /**
   * @name _removePdfHistory
   * @description
   * remove a specific key created by library 'pdfjs.history'
   * and replace the use of setItem function to use a function that avoid to set
   * the value of 'pdfjs.history' to be stored
   * @private
   */
  private _removePdfHistory(): void {
    localStorage.removeItem(this.historyKeyValue);
    const originalSetItem = localStorage.setItem;

    localStorage.setItem = (key: string, value: string) => {
      if (key !== this.historyKeyValue) {
        originalSetItem.call(localStorage, key, value);
      }
    };
  }

  /**
   * @name exit
   * @description
   * call modal controller service to close the modal
   * @memberof SharedViewerPdfComponent
   */
  exit(): void {
    this._sharedModalControllerService.closeModal();
  }

  /**
   * @name onSearch
   * @description
   * trigger the on search event to find in the pdf
   * @memberof SharedViewerPdfComponent
   * @param $event
   */
  onSearch($event: CustomEvent): void {
    const searchTerm = $event.detail.value;
    this.isTextSearch = (searchTerm ?? '').length > 0;
    this._pdfService.find(searchTerm);
  }

  /**
   * @name zoomChange
   * @description
   * calculate the zoom in percentage
   * @memberof SharedViewerPdfComponent
   * @param $event
   */
  zoomChange($event: any): void {
    this.zoomPercentage = Math.round($event * 100);

    if (!this.zoomLevel) {
      this.zoomLevel = this.zoomPercentage.toString();
    }
  }

  /**
   * @name pagesLoaded
   * @description
   * trigger the pages loaded and saved into variable
   * @memberof SharedViewerPdfComponent
   * @param event
   */
  pagesLoaded(event: PagesLoadedEvent) {
    this.pagesCount = event.pagesCount;
  }

  /**
   * @name shiftPage
   * @description
   * change the current page
   * @memberof SharedViewerPdfComponent
   * @param offset
   */
  shiftPage(offset: number) {
    this.currentPage += offset;
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.pagesCount));
  }

  /**
   * @name shiftZoom
   * @description
   * change the zoom
   * @memberof SharedViewerPdfComponent
   * @param offset
   */
  shiftZoom(offset: number): void {
    const increase = this.zoomPercentage * 0.10 * offset;
    this.zoomLevel = (this.zoomPercentage + increase).toString();
    this.zoomLevel = Math.max(10, Math.min(+this.zoomLevel, 1000)).toString();
  }

  /**
   * @name toggleSidebar
   * @description
   * show/hide the toggle sidebar from the pdf library
   * @memberof
   */
  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  /**
   * @name searchNext
   * @description
   * on search let pdf service change next page
   * @memberof SharedViewerPdfComponent
   */
  searchNext() {
    this._pdfService.findNext();
  }

  /**
   * @name searchPrev
   * @description
   * on search let pdf service change previous page
   * @memberof SharedViewerPdfComponent
   */
  searchPrev() {
    this._pdfService.findPrevious();
  }

  onResizeWorkaround(): void {
    this.viewerHeightSubject.next('100%');
    setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
  }

  async save(): Promise<void> {
    if (!this.downloadUri) {
      await this.showError(new Error("missing download uri"));
      return;
    }

    const loading = await this._loadingController.create({
      message: this._translateService.instant("SHARED.PDF_VIEWER.FILE_OPEN.LOADING"),
      spinner: "dots",
      backdropDismiss: false
    });

    const isWeb = Capacitor.getPlatform() === 'web';
    await loading.present();

    if (isWeb) {
      const blob = await this._pdfService.getCurrentDocumentAsBlob();
      if (blob) {
        this.openInWeb(blob);
      }
    } else {
      await Share
        .share({ url: this.downloadUri })
        .catch(async (error) => {
          await this.showError(error);
        });
    }

    await loading.dismiss();
  }

  private async showError(error: any): Promise<void> {
    const alert = await this._alertController.create({
      header: this._translateService.instant("GENERAL.ALERT.HEADER"),
      message: this._translateService.instant("SHARED.PDF_VIEWER.FILE_OPEN.ERROR"),
      buttons: [
        {
          text: 'Ok',
        },
        {
          text: this._translateService.instant("GENERAL.ALERT.BUTTONS.DETAILS"),
          handler: async () => {
            const alertDetail = await this._alertController.create({
              header: this._translateService.instant("GENERAL.ALERT.HEADER"),
              message: error?.toString(),
              buttons: [
                {
                  text: 'Ok',
                },
              ],
            });

            await alertDetail.present();
          }
        },
      ],
    });

    await alert.present();
  }

  private openInWeb(blob: Blob): void {
    const urlObject = URL.createObjectURL(blob);
    const aTagElement: HTMLAnchorElement = document.createElement('a');
    aTagElement.href = urlObject;
    aTagElement.download = "file.pdf";

    document.body.appendChild(aTagElement);
    aTagElement.click();
    document.body.removeChild(aTagElement);

    URL.revokeObjectURL(urlObject);
  }

  protected readonly async = async;
}
